import i18n from '../../i18n/i18n';
import {
  IAttendanceDeleteRequest,
  IAttendanceDeleteResponse,
  ICreateManualAttendanceRequest,
  ICreateManualAttendanceResponse,
} from '../../interface/attendanceModel';
import {
  createManualAttendance as createManualAttendanceService,
  deleteAttendance as deleteAttendanceService,
} from '../../services/attendance';
import { createAsyncThunk } from '@reduxjs/toolkit';
import axios, { AxiosError } from 'axios';
import {
  IPostAttendanceResponse,
  IPostAttendancePayload,
  IAttendanceError,
  ICheckoutResponseBody,
  ICheckoutRequestBody,
} from '../../interface/attendanceModel';
import {
  ChekoutAttendance,
  postAttendance as postAttendanceService,
} from '../../services/attendance';

export const postAttendance = createAsyncThunk<
  IPostAttendanceResponse,
  IPostAttendancePayload,
  { rejectValue: IAttendanceError }
>('attendance/postAttendanceCheckin', async (body, { rejectWithValue }) => {
  try {
    const response = await postAttendanceService(body);
    return response;
  } catch (err: any) {
    if (axios.isAxiosError(err)) {
      const error = err as AxiosError<IAttendanceError>;
      const errorResponse = error.response?.data;
      return rejectWithValue(
        errorResponse || {
          message: i18n.t('msg.unknown_error', i18n.t('toast.unknown_error', 'Unknown error')),
        },
      );
    } else {
      return rejectWithValue({ message: err.message });
    }
  }
});

export const postAttendanceCheckout = createAsyncThunk<
  ICheckoutResponseBody,
  ICheckoutRequestBody,
  { rejectValue: IAttendanceError }
>('attendance/postAttendanceCheckout', async (body, { rejectWithValue }) => {
  try {
    const response = await ChekoutAttendance(body);
    return response;
  } catch (err: any) {
    if (axios.isAxiosError(err)) {
      const error = err as AxiosError<IAttendanceError>;
      const errorResponse = error.response?.data;
      return rejectWithValue(
        errorResponse || {
          message: i18n.t('msg.unknown_error', i18n.t('toast.unknown_error', 'Unknown error')),
        },
      );
    } else {
      return rejectWithValue({ message: err.message });
    }
  }
});

export const deleteAttendance = createAsyncThunk<
  IAttendanceDeleteResponse,
  IAttendanceDeleteRequest,
  { rejectValue: IAttendanceError }
>('attendance/deleteAttendance', async (body, { rejectWithValue }) => {
  try {
    const response = await deleteAttendanceService(body);
    return response;
  } catch (err: any) {
    if (axios.isAxiosError(err)) {
      const error = err as AxiosError<IAttendanceError>;
      const errorResponse = error.response?.data;
      return rejectWithValue(
        errorResponse || {
          message: i18n.t('msg.unknown_error', i18n.t('toast.unknown_error', 'Unknown error')),
        },
      );
    } else {
      return rejectWithValue({ message: err.message });
    }
  }
});

/**
 * Create the manual attendance sheet for a classroom + date.
 *
 * Reads the rejection body directly rather than going through
 * `axios.isAxiosError`, as the three thunks above do. That check can never pass
 * here: api.tsx's interceptor rejects with `error.response?.data || error.message`,
 * so what arrives is the plain backend body (or a string) and never an
 * AxiosError — which is why those thunks always fall through to the `else`
 * branch and lose `status`. The caller needs the status to tell a 409 (sheet
 * already complete) from a 400 (bad date).
 */
export const createManualAttendance = createAsyncThunk<
  ICreateManualAttendanceResponse,
  ICreateManualAttendanceRequest,
  { rejectValue: IAttendanceError }
>('attendance/createManualAttendance', async (body, { rejectWithValue }) => {
  try {
    return await createManualAttendanceService(body);
  } catch (err: any) {
    if (err && typeof err === 'object') {
      return rejectWithValue({
        status: err.status,
        message: err.message || i18n.t('toast.unknown_error', 'Unknown error'),
      });
    }
    return rejectWithValue({
      message: typeof err === 'string' ? err : i18n.t('toast.unknown_error', 'Unknown error'),
    });
  }
});

export const attendanceAction = {
  postAttendance,
  postAttendanceCheckout,
  deleteAttendance,
  createManualAttendance,
};
